/* eslint-disable @typescript-eslint/no-unused-vars */
// src/utils/responseFormatter.ts
export interface TableData {
  headers: string[];
  rows: string[][];
}

export interface ResponseSection {
  title: string;
  emoji: string;
  tables: TableData[];
  content: string;
}

export interface FormattedResponse {
  sections: ResponseSection[];
}

export const parseStructuredResponse = (response: string): FormattedResponse => {
  const sections: ResponseSection[] = [];
  const lines = response.split('\n');
  
  let currentSection: ResponseSection | null = null;
  let currentTable: TableData | null = null;
  let inTable = false;
  let tableRows: string[][] = [];
  let contentBuffer: string[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Detect section headers (## with emojis or SECTION pattern)
    const sectionMatch = line.match(/^##\s+([📊🎯🛠️📚📝✅🏠📱🌟💬🌈]\s*)?(.*)/);
    if (sectionMatch && (line.includes('SECTION') || sectionMatch[2])) {
      // Save previous section
      if (currentSection) {
        if (contentBuffer.length > 0) {
          currentSection.content = contentBuffer.join('\n').trim();
        }
        sections.push(currentSection);
      }
      
      // Start new section
      const fullTitle = sectionMatch[2].trim();
      const emoji = sectionMatch[1]?.trim() || getEmojiForTitle(fullTitle);
      
      currentSection = {
        title: fullTitle,
        emoji: emoji,
        tables: [],
        content: ''
      };
      contentBuffer = [];
      currentTable = null;
      inTable = false;
      continue;
    }
    
    // Detect markdown tables
    if (line.includes('|') && line.trim().startsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      
      const cells = line.split('|')
        .filter(cell => cell.trim() !== '')
        .map(cell => cell.trim());
      
      // Skip separator rows (contains only ---, :--, etc.)
      if (cells.length > 0 && !cells.every(cell => cell.match(/^[-:]+$/))) {
        tableRows.push(cells);
      }
    } else {
      if (inTable && tableRows.length > 0) {
        if (currentSection && tableRows[0]) {
          currentSection.tables.push({
            headers: tableRows[0],
            rows: tableRows.slice(1)
          });
        }
        inTable = false;
        tableRows = [];
      }
      
      // Add to content buffer (excluding empty separator lines)
      if (currentSection && line.trim() && !line.match(/^[-|]+$/)) {
        contentBuffer.push(line);
      }
    }
  }
  
  // Handle the last section
  if (currentSection) {
    if (contentBuffer.length > 0) {
      currentSection.content = contentBuffer.join('\n').trim();
    }
    sections.push(currentSection);
  }
  
  return { sections };
};

// Helper function to get emoji based on section title
const getEmojiForTitle = (title: string): string => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('executive') || titleLower.includes('summary')) return '📊';
  if (titleLower.includes('objective') || titleLower.includes('goal')) return '🎯';
  if (titleLower.includes('strategy') || titleLower.includes('instruction')) return '🛠️';
  if (titleLower.includes('curriculum') || titleLower.includes('adaptation')) return '📚';
  if (titleLower.includes('lesson') || titleLower.includes('plan')) return '📝';
  if (titleLower.includes('assessment') || titleLower.includes('progress')) return '✅';
  if (titleLower.includes('family') || titleLower.includes('community') || titleLower.includes('home')) return '🏠';
  if (titleLower.includes('resource') || titleLower.includes('support')) return '📱';
  if (titleLower.includes('real-world') || titleLower.includes('application')) return '🌟';
  if (titleLower.includes('reflection') || titleLower.includes('next')) return '💬';
  return '📌';
};